import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightToBracket,
  faChevronDown,
  faUser,
  faVideo,
} from "@fortawesome/pro-solid-svg-icons";
import { MEMBER_LOGIN_URL, CREATOR_LOGIN_URL } from "@/constants/urls";
import { trackCta } from "@/lib/analytics/cta";

/**
 * Desktop "Login" control. Opens a small menu so the visitor can pick the
 * Members or Creators login, then routes to the right external login.
 * Implements the menu keyboard model: focus moves into the first item on open,
 * Up/Down cycle the items, and Escape closes the menu and returns focus to the
 * trigger. Also closes on outside-click.
 */
const LoginMenu = ({ inverse = false }: { inverse?: boolean }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<(HTMLAnchorElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    itemsRef.current[0]?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleItemKeyDown = (
    e: React.KeyboardEvent<HTMLAnchorElement>,
    index: number,
  ) => {
    const items = itemsRef.current.filter(Boolean) as HTMLAnchorElement[];
    if (items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(index + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(index - 1 + items.length) % items.length]?.focus();
    }
  };

  return (
    <div ref={ref} className="relative flex items-center">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 text-sm font-semibold ${
          inverse ? "text-white" : "text-black"
        } hover:opacity-80 transition-opacity duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 rounded`}
      >
        <FontAwesomeIcon icon={faArrowRightToBracket} className="w-4 h-4" />
        Login
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Log in"
          className="absolute right-0 top-full mt-2 w-60 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl ring-1 ring-black/5 z-50"
        >
          <a
            ref={(el) => {
              itemsRef.current[0] = el;
            }}
            role="menuitem"
            href={MEMBER_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackCta({ cta_id: "login_member", location: "header", destination: MEMBER_LOGIN_URL });
              setOpen(false);
            }}
            onKeyDown={(e) => handleItemKeyDown(e, 0)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors focus-visible:outline-none focus-visible:bg-zinc-50"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-100 text-black">
              <FontAwesomeIcon icon={faUser} className="w-3.5 h-3.5" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-black">Members</span>
              <span className="block text-xs text-zinc-500">portal.fivestar.video</span>
            </span>
          </a>
          <a
            ref={(el) => {
              itemsRef.current[1] = el;
            }}
            role="menuitem"
            href={CREATOR_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackCta({ cta_id: "login_creator", location: "header", destination: CREATOR_LOGIN_URL });
              setOpen(false);
            }}
            onKeyDown={(e) => handleItemKeyDown(e, 1)}
            className="flex items-center gap-3 px-4 py-3 border-t border-zinc-100 hover:bg-zinc-50 transition-colors focus-visible:outline-none focus-visible:bg-zinc-50"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-100 text-black">
              <FontAwesomeIcon icon={faVideo} className="w-3.5 h-3.5" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-black">Creators</span>
              <span className="block text-xs text-zinc-500">creator.fivestar.video</span>
            </span>
          </a>
        </div>
      )}
    </div>
  );
};

export default LoginMenu;
